import { ColumnHelper } from "@tanstack/react-table";
import { ResourceTableOption } from "PFApp/components/resource_table_toggle";
import { useProfilePillSectionCustomTypes } from "PFApp/hooks";
import { LoadingDots } from "PFComponents/loading_dots";
import Table from "PFComponents/table";
import { useInfiniteScroll } from "PFCore/hooks/use_infinite_scroll";
import { Activity } from "PFTypes";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { MatchesResponse, MatchesTableEntry } from "../../../hooks";
import { RenderActionCell, RowSelection } from "../../../resource_table_toggle";
import { createAvailabilityColumn } from "./availability_column";
import { createCustomColumn } from "./custom_column";
import { createMatchColumn } from "./match_column";
import { createWorkforceMemberColumn } from "./workforce_member_column";

type MatchesTableProps = RowSelection & {
  activity: Activity;
  matchesResponse: MatchesResponse;
  renderActionCell?: RenderActionCell;
};

export const MatchesTable = ({
  activity,
  onRowSelectionChange,
  rowSelectionState,
  matchesResponse,
  renderActionCell
}: MatchesTableProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "resourceTableToggle.matches.columns" });
  const { profilePillCustomTypes, getValue } = useProfilePillSectionCustomTypes();

  const { matchesData, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage } = matchesResponse;

  const lastRowRef = useInfiniteScroll<HTMLTableRowElement>({
    fetchMore: fetchNextPage,
    loading: isFetching,
    hasMore: hasNextPage
  });

  const columnsGenerator = useCallback(
    (columnHelper: ColumnHelper<MatchesTableEntry>) => {
      const wmColumnSize = profilePillCustomTypes.length > 0 ? 250 : 350;

      return [
        createWorkforceMemberColumn(columnHelper, {
          headerLabel: t("workforceMember"),
          activity,
          size: wmColumnSize
        }),
        ...profilePillCustomTypes.map((customType) =>
          createCustomColumn(columnHelper, {
            id: customType.name,
            headerLabel: customType.display_as ?? "",
            getValue: (entry) => getValue(entry.profile.pills, customType.id)
          })
        ),
        createMatchColumn(columnHelper, { headerLabel: t("match") }),
        createAvailabilityColumn(columnHelper, { headerLabel: t("availability") }),
        ...(renderActionCell
          ? [
              columnHelper.accessor((row) => row, {
                id: "action",
                header: () => null,
                cell: (info) =>
                  renderActionCell({
                    data: info.getValue(),
                    option: ResourceTableOption.Matches
                  }),
                enableSorting: false
              })
            ]
          : [])
      ];
    },
    [profilePillCustomTypes, getValue, activity, renderActionCell, t]
  );

  if (isFetching && !isFetchingNextPage) {
    return <LoadingDots circlesEnabled circleSize="sm" centered />;
  }

  const isSelectionEnabled = !!onRowSelectionChange;

  return (
    <>
      <Table<MatchesTableEntry>
        tableData={matchesData}
        generateColumns={columnsGenerator}
        enableRowSelection={isSelectionEnabled}
        onRowSelectionChange={onRowSelectionChange}
        rowSelection={rowSelectionState}
        disableAutoResizing
        lastRowRef={lastRowRef}
        getRowId={(entry) => `${entry.profile.id}`}
        onRowClick={
          onRowSelectionChange ? (rowData) => onRowSelectionChange({ [rowData.profile.id]: true }) : undefined
        }
      />
      {isFetchingNextPage && <LoadingDots circlesEnabled circleSize="xs" centered />}
    </>
  );
};

export type { MatchesTableEntry };
